<script>
  import {
    ConfirmModal,
    RoleModal,
    Layout,
    DuplicateModal,
    InfoBox,
    QuotaProgress,
  } from "../../components/index";
  import { ModalType } from "../../types/modal";
  import {
    faPlus,
    faTrashAlt,
    faPen,
    faClone,
  } from "@fortawesome/pro-regular-svg-icons";
  import {
    faSort,
    faSortDown,
    faSortUp,
  } from "@fortawesome/pro-solid-svg-icons";
  import {
    RolesRepository,
    ContractsRepository,
  } from "../../repositories/index";
  import {
    Button,
    Flex,
    FlexItem,
    Icon,
    Text,
    SimpleDataTable,
    DataCell,
    TextField,
    DataTableExpansion,
    DataTableExpansionContent,
    ProgressBar,
  } from "@gl/design-system";
  import { onMount, getContext } from "svelte";
  import { _, contract as contractStore, callout } from "../../stores/index";
  import { Link, navigate } from "svelte-routing";
  import { parseUrl, setUrl, setModalParams, clearModalParams } from "../../utils/index";
  import { format } from "date-fns";
  const { isAuthorized } = getContext("isAuthorized");

  const conID = $contractStore.id;

  const loading = {};

  let roles = [];
  let contract;

  let modal;
  let confirmModal;
  let duplicateModal;
  let selectedRole;
  let modalType;

  let search = {
    name: "",
  };
  let order = {
    asc: true,
  };
  let searched;

  async function fetchRoles() {
    try {
      loading.roles = true;
      const result = await RolesRepository.getRoles(conID);
      roles = result.list;
      searched = roles;
    } finally {
      loading.roles = false;
    }
  }

  async function fetchContract() {
    loading.contract = true;
    try {
      contract = await ContractsRepository.getContract($contractStore.id);
    } finally {
      loading.contract = false;
    }
  }

  function sort(field = null, toggleAsc = true) {
    if (toggleAsc) {
      order.asc = !order.asc;
    }
    order.by = field;

    const cmpA = order.asc ? -1 : 1;
    const cmpB = order.asc ? 1 : -1;
    searched = searched.sort((a, b) => {
      const A =
        field === "modified"
          ? a.metadata?.modifiedAt
          : a[order.by].toUpperCase();
      const B =
        field === "modified"
          ? b.metadata?.modifiedAt
          : b[order.by].toUpperCase();
      return A === B ? 0 : A < B ? cmpA : cmpB;
    });
    order.icon = order.asc ? "ArrowDown" : "ArrowUp";
    setUrl({
      ...search,
      orderBy: order.by,
      orderAsc: order.asc,
      roleModal: modalType,
    });
  }

  async function handleDelete(roleID) {
    try {
      await RolesRepository.deleteRole(roleID);
    } finally {
      fetchRoles();
      confirmModal = false;
      callout.set({
        msg: $_("role.delete.callout"),
        status: "success",
        timeout: true,
      });
    }
  }

  const handleSearch = () => {
    const upperCaseSearch = search.name.trim().toUpperCase();
    searched = roles.filter((role) =>
      role.name.toUpperCase().includes(upperCaseSearch),
    );
    if (order.by) {
      sort(order.by, false);
    } else {
      setUrl({
        ...search,
        orderBy: order.by,
        orderAsc: order.asc,
        roleModal: modalType,
      });
    }
  };

  const handleReset = () => {
    search = { name: "" };
    order = { asc: true };
    handleSearch();
  };

  function openModal(type, role) {
    if (type === "duplicate") {
      duplicateModal = true;
    } else {
      modal = true;
    }
    modalType = type;
    selectedRole = role;
    setModalParams("roleModal", "roleId", { type, id: role?.id });
  }

  function closeModal(type) {
    if (type === "duplicate") {
      duplicateModal = false;
    } else {
      modal = false;
    }
    selectedRole = null;
    clearModalParams("roleModal", "roleId");
  }

  function parseUrlParams() {
    const query = parseUrl(window.location.search);
    search = { ...search, name: query.name || "" };
    if (query.orderBy) {
      order.by = query.orderBy;
      order.asc = query.orderAsc === "true";
    }
    if (query.roleModal) {
      if (query.roleId) selectedRole = roles.find((r) => r.id === query.roleId);
      openModal(query.roleModal, selectedRole);
    }
    handleSearch();
  }

  onMount(async () => {
    await fetchRoles();
    fetchContract();
    parseUrlParams();
  });
</script>

{#if confirmModal}
  <ConfirmModal
    bind:active={confirmModal}
    on:close={() => {
      confirmModal = false;
      selectedRole = null;
    }}
    title={$_("delete.resource", {
      values: { resourceType: $_("common.role") },
    })}
    handleYes={() => handleDelete(selectedRole.id)}
    confirmationValue={selectedRole.id}
  >
    <p>
      {@html $_("delete.warning.access1", {
        values: {
          resourceName: `${selectedRole.name}`,
        },
      })}
    </p>

    <p class="mt-4 mb-2">
      {@html $_("delete.confirmation", {
        values: {
          resourceType: $_("common.role").toLowerCase(),
          resourceId: selectedRole.id,
        },
      })}
    </p>
  </ConfirmModal>
{/if}

<svelte:head>
  <title>
    {$_("common.roles")} | {$_("access-manager")}
  </title>
</svelte:head>

{#if modal}
  <RoleModal
    bind:active={modal}
    on:close={() => closeModal()}
    on:success={() => {
      callout.set({
        msg: selectedRole ? $_("role.edit.callout") : $_("role.new.callout"),
        status: "success",
        timeout: true,
      });
      closeModal();
      fetchRoles();
    }}
    {modalType}
    data={selectedRole}
  />
{/if}

{#if duplicateModal}
  <DuplicateModal
    bind:active={duplicateModal}
    on:close={() => closeModal("duplicate")}
    on:success={() => {
      closeModal("duplicate");
      fetchRoles();
      callout.set({
        msg: $_("group.new.callout"),
        status: "success",
        timeout: true,
      });
    }}
    modalType={ModalType.Role}
    data={selectedRole}
  />
{/if}

<Layout selected="roles">
  <Flex>
    <FlexItem>
      <h3 class="level-3 gl-title">{$_("common.roles")}</h3>
    </FlexItem>
    {#if $isAuthorized("role_create")}
      <FlexItem pushEnd>
        <Button
          on:click={() => {
            openModal(ModalType.Post);
          }}
          disabled={!contract ||
            (contract?.data?.roleQuota &&
              roles.length >= contract.data.roleQuota)}
        >
          <Icon icon={faPlus} />
          <Text>{$_("roles.add_role")}</Text>
        </Button>
      </FlexItem>
    {/if}
  </Flex>

  <p class="mb-5">
    {$_("roles.total", {
      values: { total: roles.length },
    })}
  </p>

  <Flex class="mb-5">
    <FlexItem class="mr-4" style="width:300px">
      <TextField
        variant="Search"
        bind:value={search.name}
        on:keydown={(e) => {
          if (e.key === "Enter") {
            handleSearch();
          }
        }}
      />
    </FlexItem>

    <FlexItem class="mr-4">
      <Button on:click={handleSearch}>
        {$_("common.search")}
      </Button>
    </FlexItem>

    <FlexItem>
      <Button outlined on:click={handleReset}>
        {$_("common.reset")}
      </Button>
    </FlexItem>
  </Flex>

  <div class="results p-2 mb-4">
    <FlexItem>
      <p role="status">
        <b>{searched ? searched.length : roles.length}</b>
        {$_("common.results")}
      </p>
    </FlexItem>

    {#if loading.contract === false && contract?.data?.roleQuota}
      <FlexItem pushEnd>
        <Flex class="align-items-center">
          <FlexItem>
            <b class="mr-2">{$_("quota.role")}:</b>
          </FlexItem>
          <FlexItem>
            <QuotaProgress
              amount={roles.length}
              quota={contract.data.roleQuota}
            />
          </FlexItem>
          {#if roles.length >= contract.data.roleQuota}
            <FlexItem>
              <InfoBox
                title={$_("quota.role.infobox.title")}
                tooltipLeft="null"
                tooltipRight="20%">{$_("quota.role.infobox.text")}</InfoBox
              >
            </FlexItem>
          {/if}
        </Flex>
      </FlexItem>
    {/if}
  </div>

  <div class="table-container">
    <SimpleDataTable
      let:index
      let:row
      data={searched || roles}
      fullWidth
      selectable={false}
      cellsCount="4"
      loading={loading.roles}
      key="id"
      valign="middle"
      variant="separate"
      hasExpansions={$isAuthorized("role_write") ||
        $isAuthorized("role_delete")}
      stickyHead
    >
      <slot name="empty" slot="empty">
        <div class="gl-message" style="text-align: center">
          {$_("common.no.display")}
        </div>
      </slot>
      <DataCell
        {index}
        id={`${row.id}-name`}
        header={$_("common.name")}
        headerIcon={order.by === "name" && order.asc
          ? faSortUp
          : order.by === "name" && !order.asc
            ? faSortDown
            : faSort}
        clickableHeader
        activeHeader
        on:clickHeader={() => sort("name")}
      >
        {#if $isAuthorized("user_read")}
          <Link
            to={`access-manager/contracts/${$contractStore.id}/roles/${row.id}/members`}
            ><b> {row.name}</b></Link
          >
        {:else}
          <b> {row.name}</b>
        {/if}
        <p class="text-light description" title={row.description}>
          {row.description ?? ""}
        </p>
      </DataCell>

      <DataCell
        {index}
        id={`${row.id}-modified`}
        header={$_("common.last.modified")}
        headerIcon={order.by === "modified" && order.asc
          ? faSortUp
          : order.by === "modified" && !order.asc
            ? faSortDown
            : faSort}
        clickableHeader
        activeHeader
        on:clickHeader={() => sort("modified")}
      >
        {row.metadata?.modifiedAt
          ? format(new Date(row.metadata.modifiedAt), "dd.MM.yyyy HH:mm:ss")
          : "-"}
        {#if row.metadata?.modifiedBy}
          <p class="text-light" style="font-size:14px">
            {$_("common.by")}
            {row.metadata.modifiedBy}
          </p>
        {/if}
      </DataCell>

      <DataTableExpansion slot="expansion" let:row>
        {#if row.isExpanded}
          <DataTableExpansionContent colspan={7}>
            <Flex class="justify-content-end">
              {#if $isAuthorized("group_create")}
                <FlexItem>
                  <Button
                    class="mb-2"
                    size="tiny"
                    disabled={!contract ||
                      (contract?.data?.roleQuota &&
                        roles.length >= contract.data.roleQuota)}
                    on:click={() => {
                      openModal("duplicate", row);
                    }}
                  >
                    <Icon icon={faClone} />
                    <Text>{$_("common.duplicate")}</Text>
                  </Button>
                </FlexItem>
                <FlexItem>
                  <div class="vertical-line mx-3" />
                </FlexItem>
              {/if}
              <FlexItem>
                {#if $isAuthorized("role_write")}
                  <Button
                    outlined
                    class="mr-2"
                    size="tiny"
                    on:click={() => {
                      openModal(ModalType.Put, row);
                    }}
                  >
                    <Icon icon={faPen} />
                    <Text>{$_("buttons.data")}</Text>
                  </Button>
                {/if}

                {#if $isAuthorized("role_write") && $isAuthorized("user_read")}
                  <Button
                    outlined
                    class="mr-2"
                    size="tiny"
                    on:click={() => {
                      navigate(`roles/${row.id}/members`);
                    }}
                  >
                    <Icon icon={faPen} />
                    <Text>{$_("common.members")}</Text>
                  </Button>
                {/if}

                {#if $isAuthorized("role_write") && $isAuthorized("policy_read")}
                  <Button
                    outlined
                    class="mr-2"
                    size="tiny"
                    on:click={() => {
                      navigate(`roles/${row.id}/policies`);
                    }}
                  >
                    <Icon icon={faPen} />
                    <Text>{$_("common.policies")}</Text>
                  </Button>
                {/if}

                {#if $isAuthorized("role_delete")}
                  <Button
                    outlined
                    size="tiny"
                    status="error"
                    on:click={() => {
                      selectedRole = row;
                      confirmModal = true;
                    }}
                  >
                    <Icon icon={faTrashAlt} />
                    <Text>{$_("buttons.delete")}</Text>
                  </Button>
                {/if}
              </FlexItem>
            </Flex>
          </DataTableExpansionContent>
        {/if}
      </DataTableExpansion>
    </SimpleDataTable>
  </div>
</Layout>

<style>
  .results {
    background-color: var(--gl-background);
    border-radius: 8px;
    display: flex;
  }
</style>
